/**
 * Tab title, favicon badge, PWA app badge, and haptic feedback for in-app notifications.
 */

const UNREAD_KEY = 'sheen_notification_unread';

let originalFaviconHref = null;

export function resetFaviconBadgeCache() {
  originalFaviconHref = null;
}

function getFaviconLink() {
  return (
    document.getElementById('sheen-favicon') ||
    document.querySelector('link[rel~="icon"]')
  );
}

function rememberOriginalFavicon() {
  const link = getFaviconLink();
  if (link?.href && !originalFaviconHref) {
    originalFaviconHref = link.href;
  }
}

export function getUnreadNotificationCount() {
  try {
    const n = parseInt(localStorage.getItem(UNREAD_KEY) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch (e) {
    return 0;
  }
}

export function setUnreadNotificationCount(count) {
  const n = Math.max(0, Math.floor(Number(count)) || 0);
  try {
    if (n === 0) localStorage.removeItem(UNREAD_KEY);
    else localStorage.setItem(UNREAD_KEY, String(n));
  } catch (e) {}
  return n;
}

export function incrementUnreadNotificationCount() {
  return setUnreadNotificationCount(getUnreadNotificationCount() + 1);
}

export function clearUnreadNotificationCount() {
  return setUnreadNotificationCount(0);
}

function stripTitleBadge(title) {
  return String(title || '').replace(/^\(\d{1,3}\+?\)\s+/, '');
}

export function applyDocumentTitleBadge(count) {
  const raw = document.title;
  const base = stripTitleBadge(raw);
  if (!count) {
    document.title = base;
    return;
  }
  const label = count > 99 ? '99+' : String(count);
  document.title = `(${label}) ${base}`;
}

export function applyAppBadge(count) {
  if (!('setAppBadge' in navigator)) return;
  const n = Math.min(Math.max(0, Math.floor(count) || 0), 99);
  const p = n > 0 ? navigator.setAppBadge(n) : navigator.clearAppBadge();
  if (p && typeof p.catch === 'function') p.catch(() => {});
}

export function applyFaviconBadge(count) {
  rememberOriginalFavicon();
  const link = getFaviconLink();
  if (!link || !originalFaviconHref) return;

  if (!count) {
    link.href = originalFaviconHref;
    return;
  }

  const img = new Image();
  img.onload = () => {
    try {
      const size = 32;
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, size, size);
      const cx = size - 6;
      const cy = 8;
      const r = 8;
      ctx.fillStyle = '#e74c3c';
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 9px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = count > 9 ? '9+' : String(count);
      ctx.fillText(label, cx, cy);
      link.href = canvas.toDataURL('image/png');
    } catch (e) {
      /* canvas tainted or unavailable */
    }
  };
  img.onerror = () => {};
  try {
    const u = new URL(originalFaviconHref, window.location.href);
    if (u.origin === window.location.origin) {
      img.crossOrigin = 'anonymous';
    }
  } catch (e) {}
  img.src = originalFaviconHref;
}

export function syncNotificationIndicators(count) {
  const n = Math.max(0, Math.floor(count) || 0);
  applyDocumentTitleBadge(n);
  applyFaviconBadge(n);
  applyAppBadge(n);
}

export function vibrateNotificationPulse() {
  try {
    if (typeof navigator.vibrate !== 'function') return;
    navigator.vibrate([100, 60, 100]);
  } catch (e) {}
}

export function isCoarsePointerMobile() {
  try {
    return window.matchMedia('(max-width: 768px)').matches;
  } catch (e) {
    return false;
  }
}
